import type { ActivatedRouteSnapshot } from '@angular/router';
import type { ContentPageKey, RouteTitleKey } from '../i18n/translation-catalog';
import { ROUTE_DATA } from './route-metadata';

export interface DeepestRouteData {
  readonly titleKey: RouteTitleKey | undefined;
  /** `ContentPageComponent` static-page equivalent of `titleKey` — resolved
   * via `catalog().content[contentKey].title`, checked only when `titleKey`
   * is absent (see `resolveRouteTitle()`/`route-metadata.ts`). */
  readonly contentKey: ContentPageKey | undefined;
  /** @deprecated Static fallback for routes not yet migrated to `titleKey`/`contentKey` — see `route-metadata.ts`. */
  readonly staticTitle: string | undefined;
}

/**
 * Walks to the deepest matched child of a route snapshot and returns its
 * `data.titleKey`/`data.contentKey`/`data.title`. A lazy-loaded feature
 * (`loadChildren`) nests its own leaf route data one level deeper than the
 * top-level route that imports it (e.g. `/calculator` resolves through
 * `AppShellComponent` -> the `calculator` path segment -> `CALCULATOR_ROUTES`'s
 * own `path: ''` leaf, which is where `titleKey` actually lives) — a
 * fixed-depth `.firstChild.firstChild` walk misses it. `LocalizedTitleStrategy`
 * and `AppBreadcrumbsComponent` both need this same walk, so it lives here
 * once instead of being duplicated (and previously drifting out of sync —
 * see the git history for the breadcrumb bug this fixed).
 */
export function deepestRouteData(snapshot: ActivatedRouteSnapshot): DeepestRouteData {
  let current: ActivatedRouteSnapshot | null = snapshot;
  let titleKey: RouteTitleKey | undefined;
  let contentKey: ContentPageKey | undefined;
  let staticTitle: string | undefined;
  while (current) {
    const key = current.data[ROUTE_DATA.titleKey] as RouteTitleKey | undefined;
    const cKey = current.data[ROUTE_DATA.contentKey] as ContentPageKey | undefined;
    const title = current.data[ROUTE_DATA.title] as string | undefined;
    if (key) titleKey = key;
    if (cKey) contentKey = cKey;
    if (title) staticTitle = title;
    current = current.firstChild;
  }
  return { titleKey, contentKey, staticTitle };
}
